import { useEffect, useRef, useState } from 'react'
import { Pencil } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { FormFieldLabel } from '@/components/ui/form-field-label'
import { UnderlineInput } from '@/components/ui/underline-input'

export interface ProfileUser {
  name: string
  email: string
  avatarUrl?: string
}

export interface ProfileFormValues {
  name: string
  email: string
  avatarUrl: string | null
  avatarFile?: File | null
  removeAvatar?: boolean
}

export interface ResetPasswordValues {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export interface ProfileScreenProps {
  user?: ProfileUser
  onUpdateProfile?: (
    values: ProfileFormValues,
  ) => Promise<void> | void
  onResetPassword?: (
    values: ResetPasswordValues,
  ) => Promise<void> | void
  onLogout?: () => void
  onProfileEditStart?: () => void
  onProfileCancel?: () => void
  onPasswordEditStart?: () => void
  onPasswordCancel?: () => void
  profileErrorMessage?: string | null
  passwordErrorMessage?: string | null
  /** Shop name used in the intro copy. */
  brandName?: string
}

export const demoProfileUser: ProfileUser = {
  name: 'Jess Technician',
  email: 'jess.technician@example.com',
  avatarUrl:
    'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&dpr=2',
}

function AvatarPreview(props: { user: ProfileUser }) {
  const { user } = props

  const initials = user.name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <div className="flex items-center gap-4">
      <div className="h-16 w-16 overflow-hidden rounded-full bg-slate-300 text-sm font-medium text-white">
        {user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={`${user.name} profile`}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            {initials}
          </div>
        )}
      </div>
      <div className="space-y-1 text-xs text-slate-600">
        <p className="font-medium text-slate-800">
          Profile picture
        </p>
        <p>
          This photo is shown in the header and on
          notes you leave on Callouts.
        </p>
        <p className="text-[11px] text-slate-500">
          JPG or PNG, up to 5MB. Cropped to a circle.
        </p>
      </div>
    </div>
  )
}

function AvatarEditor(props: {
  user: ProfileUser
  avatarUrl: string | null
  onUploadClick: () => void
  onRemove: () => void
}) {
  const { user, avatarUrl, onUploadClick, onRemove } = props

  const initials = user.name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <div className="flex items-center gap-4">
      <div className="h-16 w-16 overflow-hidden rounded-full bg-slate-300 text-sm font-medium text-white">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={`${user.name} profile`}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            {initials}
          </div>
        )}
      </div>
      <div className="space-y-1 text-xs text-slate-600">
        <p className="font-medium text-slate-800">
          Profile picture
        </p>
        <p>
          This photo is shown in the header and on notes you leave
          on Callouts.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          <button
            type="button"
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
            onClick={onUploadClick}
          >
            Upload new photo
          </button>
          <button
            type="button"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100"
            onClick={onRemove}
          >
            Remove photo
          </button>
        </div>
        <p className="text-[11px] text-slate-500">
          JPG or PNG, up to 5MB. Cropped to a circle.
        </p>
      </div>
    </div>
  )
}

export function ProfileScreen(props: ProfileScreenProps) {
  const user = props.user ?? demoProfileUser
  const [isProfileEditing, setIsProfileEditing] = useState(false)
  const [isPasswordEditing, setIsPasswordEditing] = useState(false)
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] =
    useState(false)
  const [avatarDraft, setAvatarDraft] = useState<{
    previewUrl: string | null
    file: File | null
    remove: boolean
  }>({
    previewUrl: user.avatarUrl ?? null,
    file: null,
    remove: false,
  })
  const [passwordDraft, setPasswordDraft] =
    useState<ResetPasswordValues>({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    })
  const [passwordValidationError, setPasswordValidationError] =
    useState<string | null>(null)
  const avatarInputRef = useRef<HTMLInputElement | null>(null)
  const avatarObjectUrlRef = useRef<string | null>(null)
  const pendingPasswordValues = useRef<ResetPasswordValues | null>(
    null,
  )

  useEffect(() => {
    if (isProfileEditing) return
    setAvatarDraft({
      previewUrl: user.avatarUrl ?? null,
      file: null,
      remove: false,
    })
  }, [isProfileEditing, user.email, user.name, user.avatarUrl])

  useEffect(() => {
    return () => {
      if (avatarObjectUrlRef.current) {
        URL.revokeObjectURL(avatarObjectUrlRef.current)
      }
    }
  }, [])

  const handleProfileSubmit: React.FormEventHandler<
    HTMLFormElement
  > = async (event) => {
    event.preventDefault()
    if (!props.onUpdateProfile) return

    try {
      await props.onUpdateProfile({
        name: user.name,
        email: user.email,
        avatarUrl: avatarDraft.previewUrl,
        avatarFile: avatarDraft.file,
        removeAvatar: avatarDraft.remove,
      })
      setIsProfileEditing(false)
    } catch (error) {
      return
    }
  }

  const handlePasswordSubmit: React.FormEventHandler<
    HTMLFormElement
  > = (event) => {
    event.preventDefault()
    if (!props.onResetPassword) return
    if (passwordDraft.newPassword !== passwordDraft.confirmPassword) {
      setPasswordValidationError(
        'New password and confirmation do not match.',
      )
      return
    }
    setPasswordValidationError(null)
    pendingPasswordValues.current = { ...passwordDraft }
    setIsPasswordDialogOpen(true)
  }

  const handleProfileEditStart = () => {
    setAvatarDraft({
      previewUrl: user.avatarUrl ?? null,
      file: null,
      remove: false,
    })
    setIsProfileEditing(true)
    props.onProfileEditStart?.()
  }

  const handleProfileCancel = () => {
    if (avatarObjectUrlRef.current) {
      URL.revokeObjectURL(avatarObjectUrlRef.current)
      avatarObjectUrlRef.current = null
    }
    if (avatarInputRef.current) {
      avatarInputRef.current.value = ''
    }
    setAvatarDraft({
      previewUrl: user.avatarUrl ?? null,
      file: null,
      remove: false,
    })
    setIsProfileEditing(false)
    props.onProfileCancel?.()
  }

  const handlePasswordEditStart = () => {
    setPasswordDraft({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    })
    setPasswordValidationError(null)
    setIsPasswordEditing(true)
    props.onPasswordEditStart?.()
  }

  const handlePasswordCancel = () => {
    setPasswordDraft({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    })
    setPasswordValidationError(null)
    setIsPasswordEditing(false)
    props.onPasswordCancel?.()
  }

  const handleAvatarUpload = () => {
    avatarInputRef.current?.click()
  }

  const handleAvatarChange: React.ChangeEventHandler<
    HTMLInputElement
  > = (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (avatarObjectUrlRef.current) {
      URL.revokeObjectURL(avatarObjectUrlRef.current)
    }

    const nextUrl = URL.createObjectURL(file)
    avatarObjectUrlRef.current = nextUrl
    setAvatarDraft({
      previewUrl: nextUrl,
      file,
      remove: false,
    })
  }

  const handleAvatarRemove = () => {
    if (avatarObjectUrlRef.current) {
      URL.revokeObjectURL(avatarObjectUrlRef.current)
      avatarObjectUrlRef.current = null
    }
    if (avatarInputRef.current) {
      avatarInputRef.current.value = ''
    }
    setAvatarDraft({
      previewUrl: null,
      file: null,
      remove: true,
    })
  }

  const handlePasswordConfirm = async () => {
    const values = pendingPasswordValues.current
    if (!values || !props.onResetPassword) return

    try {
      await props.onResetPassword(values)
    } catch (error) {
      setIsPasswordDialogOpen(false)
      return
    }
    pendingPasswordValues.current = null
    setIsPasswordDialogOpen(false)
    setIsPasswordEditing(false)
    setPasswordDraft({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    })
    props.onLogout?.()
  }

  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6">
        <header className="flex items-center justify-between gap-4">
          <div className="space-y-1">
          <h1 className="text-2xl font-semibold">
            My profile
          </h1>
          <p className="text-sm text-slate-600">
            Update your name, email and profile picture, and reset
            your password used to sign into {props.brandName || 'the app'}.
          </p>
          </div>

          {props.onLogout ? (
            <button
              type="button"
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
              onClick={props.onLogout}
            >
              Log out
            </button>
          ) : null}
        </header>

        <section className="space-y-6">
          <div className="rounded-md bg-white p-6 shadow-sm">
            <form
              className="flex flex-col gap-6"
              onSubmit={handleProfileSubmit}
            >
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <FormCapsLabel>Profile</FormCapsLabel>
                </div>

                {isProfileEditing ? (
                  <>
                    <AvatarEditor
                      user={user}
                      avatarUrl={
                        avatarDraft.remove
                          ? null
                          : avatarDraft.previewUrl ??
                            user.avatarUrl ??
                            null
                      }
                      onUploadClick={handleAvatarUpload}
                      onRemove={handleAvatarRemove}
                    />
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarChange}
                    />
                  </>
                ) : (
                  <div className="space-y-3">
                    <AvatarPreview user={user} />
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                      onClick={handleProfileEditStart}
                      aria-label="Edit profile photo"
                    >
                      <Pencil className="h-4 w-4" />
                      Edit photo
                    </button>
                  </div>
                )}

                <div className="space-y-3 pt-4 text-xs text-slate-700">
                  <div className="flex items-baseline gap-3">
                    <FormFieldLabel className="min-w-[7rem]">
                      Name
                    </FormFieldLabel>
                    <p className="text-sm text-slate-800">
                      {user.name}
                    </p>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <FormFieldLabel className="min-w-[7rem]">
                      Email
                    </FormFieldLabel>
                    <p className="text-sm text-slate-800">
                      {user.email}
                    </p>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Name and email changes require admin access.
                  </p>
                </div>
              </div>

              {isProfileEditing ? (
                <div className="flex justify-end gap-2 pt-2">
                  {props.profileErrorMessage ? (
                    <p className="mr-auto text-xs text-red-600">
                      {props.profileErrorMessage}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                    onClick={handleProfileCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    Save profile
                  </button>
                </div>
              ) : null}
            </form>
          </div>

          <div className="rounded-md bg-white p-6 shadow-sm">
            <form
              className="flex flex-col gap-6"
              onSubmit={handlePasswordSubmit}
            >
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <FormCapsLabel>Security</FormCapsLabel>
                  {!isPasswordEditing ? (
                    <button
                      type="button"
                      className="rounded-md border border-slate-200 p-1.5 text-slate-500 hover:border-slate-300 hover:bg-slate-50"
                      onClick={handlePasswordEditStart}
                      aria-label="Edit password"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
                <p className="text-xs text-slate-600">
                  Reset your password for logging into the desktop
                  and mobile views. In the real app this will also
                  log you out of other devices.
                </p>

                {isPasswordEditing ? (
                  <div className="space-y-3 text-xs">
                    <div className="flex items-end gap-3">
                      <FormFieldLabel className="min-w-[7rem]">
                        Current password
                      </FormFieldLabel>
                      <UnderlineInput
                        name="currentPassword"
                        className="w-auto flex-1"
                        type="password"
                        placeholder="Enter your current password"
                        value={passwordDraft.currentPassword}
                        onChange={(event) =>
                          setPasswordDraft((prev) => ({
                            ...prev,
                            currentPassword: event.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="flex items-end gap-3">
                      <FormFieldLabel className="min-w-[7rem]">
                        New password
                      </FormFieldLabel>
                      <UnderlineInput
                        name="newPassword"
                        className="w-auto flex-1"
                        type="password"
                        placeholder="Choose a new password"
                        value={passwordDraft.newPassword}
                        onChange={(event) => {
                          setPasswordValidationError(null)
                          setPasswordDraft((prev) => ({
                            ...prev,
                            newPassword: event.target.value,
                          }))
                        }}
                      />
                    </div>

                    <div className="flex items-end gap-3">
                      <FormFieldLabel className="min-w-[7rem]">
                        Confirm password
                      </FormFieldLabel>
                      <UnderlineInput
                        name="confirmPassword"
                        className="w-auto flex-1"
                        type="password"
                        placeholder="Re-enter the new password"
                        value={passwordDraft.confirmPassword}
                        onChange={(event) => {
                          setPasswordValidationError(null)
                          setPasswordDraft((prev) => ({
                            ...prev,
                            confirmPassword: event.target.value,
                          }))
                        }}
                      />
                    </div>

                    {passwordValidationError ||
                    props.passwordErrorMessage ? (
                      <p className="pt-1 text-xs text-red-600">
                        {passwordValidationError ??
                          props.passwordErrorMessage}
                      </p>
                    ) : null}

                    <p className="pt-1 text-[11px] text-slate-500">
                      Use at least 8 characters, with a mix of
                      letters and numbers. In the full
                      implementation, specific password rules for
                      your company may apply.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
                    <p className="font-medium text-slate-700">
                      Password
                    </p>
                    <p className="pt-1 text-sm tracking-[0.2em]">
                      ••••••••
                    </p>
                  </div>
                )}
              </div>

              {isPasswordEditing ? (
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                    onClick={handlePasswordCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    Update password
                  </button>
                </div>
              ) : null}
            </form>
          </div>
        </section>
      </main>

      <Dialog
        open={isPasswordDialogOpen}
        onOpenChange={setIsPasswordDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update password?</DialogTitle>
            <DialogDescription>
              Updating your password will sign you out. You’ll need
              to log in again with your new password.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
              onClick={() => setIsPasswordDialogOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              onClick={handlePasswordConfirm}
            >
              Confirm &amp; log out
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
